// Phase 40 Plan 02 Task 1 + Plan 03 Task 2 — Auth context provider.
// CONTEXT D-02, D-09, D-13, D-14, D-15, D-16. RESEARCH.md Pattern 1, 5, 6.
//
// Subscribes to supabase.auth.onAuthStateChange and exposes the resolved
// session to the rest of the app via React Context. Plan 04 will extend
// this with deleteAccount.
//
// Plan 03 wires:
//   - Real openConflictModal returning a Promise<'cloud' | 'local'>, backed
//     by the in-context MultiDeviceConflictModal mount.
//   - syncStatus state ({status, count}) updated by drainWal callbacks.
//   - deadLetterCount derived from getDeadLetterCount() polled on mount.
//   - scheduleWalDrain registered when session is non-null AND first-sign-in
//     upload is no longer active. stopWalDrain on sign-out.

import React, { createContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  supabase,
  signInWithProvider,
  signInWithMagicLink,
  signOut as sbSignOut,
} from './supabaseClient.js';
import { firstSignInUpload, scheduleWalDrain, stopWalDrain, getApiBase } from '../lib/sync.js';
import { scheduleEventsFlush, stopEventsFlush } from '../lib/eventsSync.js';
import { getDeadLetterCount } from '../lib/storage.js';
import MultiDeviceConflictModal from '../modals/MultiDeviceConflictModal.jsx';

// Plan 04 default-context placeholder — when AuthContext is consumed without
// a Provider (test isolation, etc.), deleteAccount rejects loudly so a UI
// surface that calls it without being wrapped surfaces the misuse.
async function deleteAccountStub() {
  return Promise.reject(new Error('deleteAccount called without AuthProvider'));
}

// Tend localStorage keys that must be cleared on account deletion. Mirrors
// the Phase 31.1 "Delete all data" key list — extended with auth + WAL keys
// added in Phase 40 so a fresh sign-in lands at clean onboarding.
const TEND_LOCAL_KEYS_TO_CLEAR_ON_DELETE = [
  'tend:supabase-user-id',
  'tend:plants',
  'tend:areas',
  'tend:completions',
  'tend:dismissals',
  'tend:notes',
  'tend:inventory',
  'tend:wal',
  'tend:wal-deadletter',
  'tend:onboarded',
  'tend:gardenSpaces',
  'tend:experience',
  'tend:location',
  'tend:locationLatLon',
  'tend:reportedImages',
];

export const AuthContext = createContext({
  user: null,
  session: null,
  jwt: null,
  loading: true,
  tier: null,
  firstSignInState: { active: false, status: 'idle', plantCount: 0, areaCount: 0, errorMessage: null },
  syncedToastVisible: false,
  syncStatus: { status: 'synced', count: 0 },
  deadLetterCount: 0,
  signIn: signInWithProvider,
  signInWithMagicLink,
  signOut: sbSignOut,
  deleteAccount: deleteAccountStub,
  dismissFirstSignInError: () => {},
  retryFirstSignInUpload: () => {},
  refreshDeadLetterCount: () => {},
});

function readLocalSnapshot() {
  try {
    return {
      plants: JSON.parse(localStorage.getItem('tend:plants') || '[]'),
      areas: JSON.parse(localStorage.getItem('tend:areas') || '[]'),
      completions: JSON.parse(localStorage.getItem('tend:completions') || '{}'),
      notes: JSON.parse(localStorage.getItem('tend:notes') || '{}'),
      inventory: JSON.parse(localStorage.getItem('tend:inventory') || '{}'),
    };
  } catch {
    return { plants: [], areas: [], completions: {}, notes: {}, inventory: {} };
  }
}

function applyCloudSnapshot(snap) {
  // Atomic localStorage rewrite — cloud becomes source of truth post-upload.
  try {
    if (Array.isArray(snap.plants))    localStorage.setItem('tend:plants',      JSON.stringify(snap.plants));
    if (Array.isArray(snap.areas))     localStorage.setItem('tend:areas',       JSON.stringify(snap.areas));
    if (snap.completions)              localStorage.setItem('tend:completions', JSON.stringify(snap.completions));
    if (snap.notes)                    localStorage.setItem('tend:notes',       JSON.stringify(snap.notes));
    if (snap.inventory)                localStorage.setItem('tend:inventory',   JSON.stringify(snap.inventory));
  } catch { /* QuotaExceeded — leave whatever wrote OK; upload still succeeded */ }
}

export default function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    session: null,
    jwt: null,
    loading: true,
  });
  const [firstSignInState, setFirstSignInState] = useState({
    active: false,
    status: 'idle', // idle | uploading | error
    plantCount: 0,
    areaCount: 0,
    errorMessage: null,
  });
  const [syncedToastVisible, setSyncedToastVisible] = useState(false);
  const [syncStatus, setSyncStatus] = useState({ status: 'synced', count: 0 });
  const [deadLetterCount, setDeadLetterCount] = useState(() => {
    try { return getDeadLetterCount(); } catch { return 0; }
  });
  // Plan 04 — tier exposed via /auth/me (read after first-sign-in upload
  // completes, refreshed on session change). Consumed by PlantCapBanner.
  const [tier, setTier] = useState(null);
  // { local, cloud, resolve } | null — set when firstSignInUpload calls
  // openConflictModal; resolved by the modal's onChoose callback.
  const [conflictPending, setConflictPending] = useState(null);
  const drainScheduledFor = useRef(null);
  const eventsFlushScheduledFor = useRef(null);

  const refreshDeadLetterCount = useCallback(() => {
    try { setDeadLetterCount(getDeadLetterCount()); } catch {}
  }, []);

  // Plan 04 — tier polling helper. Calls /auth/me once and surfaces the
  // returned tier into local state. Failures are non-fatal — UI defaults to
  // tier-undefined which hides PlantCapBanner.
  const refreshTier = useCallback(async (jwt) => {
    if (!jwt) { setTier(null); return; }
    try {
      const res = await fetch(`${getApiBase()}/auth/me`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.tier === 'free' || data?.tier === 'premium' || data?.tier === 'admin') {
        setTier(data.tier);
      }
    } catch { /* non-fatal */ }
  }, []);

  // Plan 04 — real signOut wrapper. T-40-02-07 mitigation: clear
  // tend:supabase-user-id so a different account signing in on the same
  // device triggers a fresh first-sign-in upload flow.
  const signOut = useCallback(async () => {
    try { await sbSignOut(); } catch { /* sb may already be signed out */ }
    try { localStorage.removeItem('tend:supabase-user-id'); } catch {}
    setTier(null);
  }, []);

  // Plan 04 — real deleteAccount. POST /account/delete (worker handler
  // performs R2 cleanup + auth.admin.deleteUser cascade + KV invalidate),
  // then sign out + clear Tend localStorage + redirect to onboarding so
  // the next mount starts fresh.
  const deleteAccount = useCallback(async () => {
    if (!state.session?.access_token) throw new Error('not-signed-in');
    const res = await fetch(`${getApiBase()}/account/delete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${state.session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const err = await res.json();
        detail = err?.error || detail;
      } catch { /* keep default */ }
      throw new Error(detail);
    }
    // Sign out (clears sb-* localStorage)
    try { await sbSignOut(); } catch { /* non-fatal */ }
    // Clear Tend localStorage so the user lands at fresh onboarding.
    for (const k of TEND_LOCAL_KEYS_TO_CLEAR_ON_DELETE) {
      try { localStorage.removeItem(k); } catch { /* noop */ }
    }
    // Hard reload to bring up the onboarding flow without dragging stale state.
    try { window.location.replace(import.meta.env.BASE_URL || '/'); } catch { /* noop in tests */ }
  }, [state.session]);

  // Plan 03 — real openConflictModal. Returns Promise<'cloud' | 'local'>.
  // Mounts MultiDeviceConflictModal in this provider's tree (below).
  //
  // 2026-05-11 — flip firstSignInState.status to 'conflict-pending' while
  // the user is choosing, then back to 'uploading' once they have. App.jsx
  // unmounts FirstSignInUploadModal during 'conflict-pending' so the
  // (lower-zIndex) MultiDeviceConflictModal isn't occluded by the upload
  // spinner. Without this, the spinner (zIndex 2100) sits on top of the
  // conflict modal (zIndex 2000) and intercepts every tap. See Phase 40.2.
  const openConflictModal = useCallback(({ local, cloud }) => {
    setFirstSignInState((s) => ({ ...s, status: 'conflict-pending' }));
    return new Promise((resolve) => {
      setConflictPending({
        local,
        cloud,
        resolve: (choice) => {
          setFirstSignInState((s) => ({ ...s, status: 'uploading' }));
          resolve(choice);
        },
      });
    });
  }, []);

  const runFirstSignInUpload = useCallback(async (session) => {
    if (!session?.user?.id) return;
    const localSnap = readLocalSnapshot();
    setFirstSignInState({
      active: true,
      status: 'uploading',
      plantCount: localSnap.plants.length,
      areaCount: localSnap.areas.length,
      errorMessage: null,
    });

    const result = await firstSignInUpload({
      jwt: session.access_token,
      plants: localSnap.plants,
      areas: localSnap.areas,
      completions: localSnap.completions,
      notes: localSnap.notes,
      inventory: localSnap.inventory,
      openConflictModal,
    });

    if (result.mode === 'failed') {
      setFirstSignInState((s) => ({
        ...s,
        status: 'error',
        errorMessage: result.message || "Couldn't save to cloud, your data is safe locally.",
      }));
      return;
    }

    // Apply cloud snapshot only on paths where the cloud is the new source
    // of truth (downloaded / conflict-resolved-cloud), OR when the upload
    // round-trip echoed back substantive data. The previous `if (plants ||
    // areas)` check fired on EMPTY arrays (truthy in JS) — so a successful
    // upload whose downloadFromCloud follow-up returned [] (RLS race, fresh
    // garden, transient REST hiccup) silently overwrote good local data
    // with empty arrays. Guard against that by requiring non-empty cloud
    // payload OR an explicit cloud-wins mode.
    const cloudHasPayload =
      (Array.isArray(result.plants) && result.plants.length > 0) ||
      (Array.isArray(result.areas) && result.areas.length > 0);
    const cloudWinsMode =
      result.mode === 'downloaded' || result.mode === 'conflict-resolved-cloud';
    if (cloudHasPayload || cloudWinsMode) {
      applyCloudSnapshot(result);
    }

    // One-time-per-account guard — write AFTER successful sync so a failed
    // attempt doesn't permanently mark the account as synced.
    try { localStorage.setItem('tend:supabase-user-id', session.user.id); } catch {}

    setFirstSignInState({ active: false, status: 'idle', plantCount: 0, areaCount: 0, errorMessage: null });
    if (result.mode !== 'noop') {
      setSyncedToastVisible(true);
      setTimeout(() => setSyncedToastVisible(false), 4000);
    }
    // Refresh dead-letter count post-upload — initial upload doesn't add
    // dead-letter entries, but this primes the indicator state.
    try { setDeadLetterCount(getDeadLetterCount()); } catch {}
  }, [openConflictModal]);

  const dismissFirstSignInError = useCallback(() => {
    setFirstSignInState({ active: false, status: 'idle', plantCount: 0, areaCount: 0, errorMessage: null });
  }, []);

  const retryFirstSignInUpload = useCallback(() => {
    if (state.session) runFirstSignInUpload(state.session);
  }, [state.session, runFirstSignInUpload]);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const s = data?.session || null;
      setState({
        user: s?.user || null,
        session: s,
        jwt: s?.access_token || null,
        loading: false,
      });
    }).catch(() => {
      if (mounted) setState((s) => ({ ...s, loading: false }));
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      setState({
        user: session?.user || null,
        session,
        jwt: session?.access_token || null,
        loading: false,
      });
      if (event === 'SIGNED_IN' && session?.user?.id) {
        let alreadySynced = false;
        try { alreadySynced = localStorage.getItem('tend:supabase-user-id') === session.user.id; } catch {}
        if (!alreadySynced) runFirstSignInUpload(session);
      }
      if (event === 'SIGNED_OUT') {
        // Detach the WAL drain interval/listener — Plan 03.
        try { stopWalDrain(); } catch {}
        // Plan 04 Task 4 — also detach the events-sync timer.
        try { stopEventsFlush(); } catch {}
        drainScheduledFor.current = null;
        eventsFlushScheduledFor.current = null;
        setTier(null);
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [runFirstSignInUpload]);

  // Plan 03 — schedule WAL drain when session is active AND first-sign-in
  // upload has completed. Idempotent — scheduleWalDrain bails on second
  // invocation per session.
  useEffect(() => {
    if (!state.session || firstSignInState.active) return;
    const userKey = state.session?.user?.id;
    if (!userKey) return;
    if (drainScheduledFor.current === userKey) return;
    drainScheduledFor.current = userKey;
    try {
      scheduleWalDrain({
        jwt: state.session.access_token,
        onStatusChange: (next) => {
          setSyncStatus(next);
          if (next?.status === 'failed' || next?.status === 'synced') {
            try { setDeadLetterCount(getDeadLetterCount()); } catch {}
          }
        },
      });
    } catch {}
  }, [state.session, firstSignInState.active]);

  // Plan 04 — refresh tier from /auth/me on session-active transitions, and
  // schedule the 60s events-sync flush. Both are idempotent per-user;
  // cleanup runs on SIGNED_OUT in the listener.
  useEffect(() => {
    if (!state.session || firstSignInState.active) return;
    const userKey = state.session?.user?.id;
    if (!userKey) return;
    if (eventsFlushScheduledFor.current !== userKey) {
      eventsFlushScheduledFor.current = userKey;
      try { scheduleEventsFlush({ jwt: state.session.access_token }); } catch {}
    }
    refreshTier(state.session.access_token);
  }, [state.session, firstSignInState.active, refreshTier]);

  const value = {
    user: state.user,
    session: state.session,
    jwt: state.jwt,
    loading: state.loading,
    tier,
    firstSignInState,
    syncedToastVisible,
    syncStatus,
    deadLetterCount,
    signIn: signInWithProvider,
    signInWithMagicLink,
    signOut,
    deleteAccount,
    dismissFirstSignInError,
    retryFirstSignInUpload,
    refreshDeadLetterCount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {conflictPending && (
        <MultiDeviceConflictModal
          local={conflictPending.local}
          cloud={conflictPending.cloud}
          onChoose={(choice) => {
            const r = conflictPending.resolve;
            setConflictPending(null);
            r(choice);
          }}
        />
      )}
    </AuthContext.Provider>
  );
}
